import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarCheck2, PhoneIncoming, PhoneCall } from "lucide-react";
import { useLang } from "../i18n";
import { Reveal } from "./Reveal";
import { LogoMark } from "./Logo";

const PhoneMockup = () => {
  const { t } = useLang();
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const iv = setInterval(() => setStage((s) => (s + 1) % 3), 2800);
    return () => clearInterval(iv);
  }, []);

  const stages = [
    { icon: <PhoneIncoming className="h-8 w-8 text-cyan-300 animate-ring-shake" />, title: t.how.phone.incoming, tone: "text-cyan-300" },
    { icon: <PhoneCall className="h-8 w-8 text-emerald-300" />, title: t.how.phone.ai, tone: "text-emerald-300", bars: true },
    { icon: <CalendarCheck2 className="h-8 w-8 text-emerald-400" />, title: t.how.phone.done, tone: "text-emerald-400", sub: t.how.phone.cal },
  ];

  return (
    <div className="relative mx-auto w-64 sm:w-72" data-testid="phone-mockup">
      <div className="absolute -inset-6 rounded-[3rem] bg-emerald-500/10 blur-2xl" />
      <div className="relative rounded-[2.4rem] border border-slate-700/70 bg-[#0B1220] p-3 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
        <div className="rounded-[1.9rem] border border-slate-800 bg-[#070B11] px-5 py-8">
          <div className="mx-auto mb-8 h-1.5 w-16 rounded-full bg-slate-800" />
          <div className="flex min-h-[16rem] flex-col items-center justify-center text-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={stage}
                initial={{ opacity: 0, y: 24, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -24, scale: 0.95 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col items-center"
              >
                <div className="relative mb-5 flex h-20 w-20 items-center justify-center rounded-full border border-slate-700/70 bg-slate-900/70">
                  {stage === 0 && (
                    <span className="animate-pulse-ring absolute inset-0 rounded-full border-2 border-cyan-400/50" />
                  )}
                  {stages[stage].icon}
                </div>
                <p className={`font-display text-base font-bold ${stages[stage].tone}`}>
                  {stages[stage].title}
                </p>
                {stages[stage].bars && (
                  <div className="mt-5 flex h-8 items-center gap-1">
                    {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                      <span
                        key={i}
                        className="eq-bar w-1 rounded-full bg-gradient-to-t from-cyan-400 to-emerald-400"
                        style={{ height: "100%", animationDelay: `${i * 0.12}s` }}
                      />
                    ))}
                  </div>
                )}
                {stages[stage].sub && (
                  <p className="mt-3 rounded-full bg-emerald-500/10 px-3 py-1 font-mono2 text-[10px] uppercase tracking-widest text-emerald-300">
                    {stages[stage].sub}
                  </p>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="mt-6 flex items-center justify-center gap-2">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  i === stage ? "w-6 bg-emerald-400" : "w-1.5 bg-slate-700"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="absolute -right-4 top-10 hidden sm:block">
        <LogoMark size={44} />
      </div>
    </div>
  );
};

export const HowItWorks = () => {
  const { t } = useLang();
  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="grid items-center gap-16 lg:grid-cols-2">
          <div>
            <Reveal>
              <p className="font-mono2 text-xs uppercase tracking-[0.28em] text-cyan-400">{t.how.kicker}</p>
              <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
                {t.how.title}
              </h2>
              <p className="mt-4 text-base text-slate-400 sm:text-lg">{t.how.sub}</p>
            </Reveal>
            <div className="mt-12 space-y-2">
              {t.how.steps.map((s, i) => (
                <Reveal key={i} delay={i * 0.12}>
                  <div
                    data-testid={`how-step-${i + 1}`}
                    className="group relative flex gap-6 rounded-2xl border border-transparent p-5 transition-colors hover:border-slate-800 hover:bg-slate-900/40"
                  >
                    <span className="font-mono2 text-sm font-semibold text-emerald-400/80 pt-1">
                      0{i + 1}
                    </span>
                    <div>
                      <h3 className="font-display text-xl font-bold tracking-tight text-slate-100 transition-colors group-hover:text-emerald-300">
                        {s.t}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.2}>
            <PhoneMockup />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
